import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const API_KEY = "AIzaSyAB4OAf79hxCJ_rVOO0WC-iulXFynkPQOY";

/* =========================
   SYSTEM INSTRUCTION
========================= */
const SYSTEM_INSTRUCTION = `
You are **Griach Tahar AI**.
You represent my web development services.
ALL answers MUST be SHORT and clear.
No long explanations.
----------------------------------
MAIN RULES (MANDATORY)
----------------------------------
1️⃣ SHORT ANSWERS ONLY
• Maximum 2–3 short sentences
• No storytelling
• No unnecessary details
2️⃣ FIRST MESSAGE RULE
When a customer starts chatting, ask:
"What is your business or idea?"
----------------------------------
ALLOWED DOMAIN ONLY
----------------------------------
• You may talk ONLY about:
        * information about me (full-stack web developer.
        * I know **HTML, CSS, JavaScript, PHP, React, Laravel, MySQL, Express, MongoDB** and the **MERN stack**.
        * I use **Figma** for UI/UX, **Merise/UML** for architecture, and **Git / GitLab** for version control.
        * I hold a **Specialized Technician Diploma in Full-Stack Web Development** and a **Baccalaureate in Physical Science**.
        * I have worked as a **full-stack developer, optical fiber tester, forklift driver, fast food chef, and operator**.
        * I speak **Arabic and French** fluently and code with a focus on clarity, maintainability, and real-world application.
        )
• Websites
• Web development
• Business / brand online presence
• Technologies for building websites
• design ui/ux design logo and any kidn of graphic design
• pricing: for websites (starting from $50 and upwards based on project complexity) and design services ( starting from $5 and upwards based on project complexity)
• timelines and delivery ( usually 1-4 weeks based on project complexity)
----------------------------------
OUTSIDE DOMAIN RULE
----------------------------------
If asked anything outside this domain, reply ONLY with:
"I don’t know. Please keep questions within my professional domain."
----------------------------------
BUSINESS FLOW
----------------------------------
After understanding the business:
• Recommend the type of website
• Propose technologies
• Keep it simple and business-focused
Example:
"I recommend a modern website to promote your brand. React and Laravel would be a good fit."
----------------------------------
CONTACT SHARING RULE
----------------------------------
Share contact info ONLY IF:
✅ Customer says: "I want you to build it"
OR
✅ Customer asks for contact details
Then reply EXACTLY with:
"You can contact me here:
📧 Email: tahargriach@gmail.com
📞 Phone: +212689789885"
----------------------------------
STYLE
----------------------------------
• Short
• Professional
• Friendly
• Confident
• No guessing
• No extra words
`;

export default function App() {
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const messagesContainerRef = useRef(null);

  /* =========================
     INITIAL MESSAGE
  ========================= */
  useEffect(() => {
    setMessages([
      {
        role: "bot",
        text: "👋 Hi! I'm Griach Tahar, a web developer based in Morocco.\n\nI help businesses and brands grow with modern, fast websites.\n\nWhat’s your business or idea?",
      },
    ]);
  }, []);

  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [messages, loading]);

  const sendMessage = async () => {
    if (!input.trim()) return;
    const userMessage = { role: "user", text: input };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput("");
    setLoading(true);
    try {
      const apiContents = [
        { role: "user", parts: [{ text: SYSTEM_INSTRUCTION }] },
        ...updatedMessages.map((m) => ({
          role: m.role === "bot" ? "model" : "user",
          parts: [{ text: m.text }],
        })),
      ];
      const res = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${API_KEY}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ contents: apiContents }),
        }
      );
      const data = await res.json();
      const reply =
        data.candidates?.[0]?.content?.parts?.[0]?.text ||
        "Sorry, something went wrong.";
      setMessages((prev) => [...prev, { role: "bot", text: reply }]);
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: "bot", text: "Connection error. Please try again." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        :root {
          --primary-green: #10b981;
          --primary-teal: #14b8a6;
          --primary-blue: #0ea5e9;
          --primary-indigo: #6366f1;
          --primary-violet: #8b5cf6;
          --white: #ffffff;
          --gray-50: #f9fafb;
          --gray-100: #f3f4f6;
          --gray-200: #e5e7eb;
          --gray-300: #d1d5db;
          --gray-500: #6b7280;
          --gray-800: #1f2937;
          --yellow-400: #fbbf24;
          --red-500: #ef4444;
          --shadow-lg: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
          --shadow-xl: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
          --shadow-2xl: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          --glow: 0 0 12px rgba(14, 165, 233, 0.5);
        }
       
        .floating-button {
          position: fixed;
          bottom: 32px;
          right: 32px;
          z-index: 50;
          display: flex;
          height: 72px;
          width: 72px;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          background: linear-gradient(145deg, var(--primary-green), var(--primary-teal), var(--primary-blue));
          color: var(--white);
          box-shadow: var(--shadow-2xl), var(--glow);
          border: 2px solid rgba(255, 255, 255, 0.3);
          cursor: pointer;
          transition: all 0.4s ease;
          animation: rotate-glow 4s linear infinite;
        }
       
        .floating-button:hover {
          box-shadow: var(--shadow-2xl), 0 0 24px rgba(14, 165, 233, 0.7);
          transform: scale(1.1);
        }
       
        .floating-badge {
          position: absolute;
          top: -4px;
          right: -4px;
          background: var(--yellow-400);
          color: var(--gray-800);
          font-size: 14px;
          font-weight: bold;
          border-radius: 50%;
          height: 28px;
          width: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: var(--shadow-lg), 0 0 8px rgba(251, 191, 36, 0.5);
          animation: bounce 1.2s infinite;
         
         
        }
       
        .backdrop {
          position: fixed;
          inset: 0;
          z-index: 30;
          background: rgba(0, 0, 0, 0.4);
          backdrop-filter: blur(10px);
        }
       
        @media (min-width: 768px) {
          .backdrop {
            background: transparent;
            backdrop-filter: none;
          }
        .floating-button {
          bottom: 16px;
          right: 16px;
          height: 50px;
          width: 50px;
        }
        }
       
        .chat-container {
          position: fixed;
          bottom: 112px;
          right: 32px;
          z-index: 40;
          width: calc(100vw - 64px);
          max-width: 400px;
          border-radius: 28px;
          background: linear-gradient(160deg, rgba(255, 255, 255, 0.95), rgba(243, 244, 246, 0.95));
          backdrop-filter: blur(40px);
          box-shadow: var(--shadow-2xl), var(--glow);
          border: 1px solid rgba(229, 231, 235, 0.6);
          overflow: hidden;
        }
       
        @media (min-width: 768px) {
          .chat-container {
            max-width: 480px;
          }
        }
       
        .chat-header {
          background: linear-gradient(100deg, var(--primary-green), var(--primary-teal), var(--primary-blue), var(--primary-indigo));
          padding: 24px 28px;
          border-top-left-radius: 28px;
          border-top-right-radius: 28px;
          z-index: 5;
        }
       
        .header-content {
          display: flex;
          align-items: center;
          gap: 20px;
        }
       
        .avatar {
          height: 60px;
          width: 60px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.2);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--white);
          font-size: 28px;
          box-shadow: 0 0 20px rgba(255, 255, 255, 0.4);
          animation: avatar-glow 2s infinite;
        }
       
        .header-text {
          flex: 1;
        }
       
        .header-name {
          font-size: 22px;
          font-weight: bold;
          color: var(--white);
          margin: 0;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
        }
       
        .header-title {
          font-size: 15px;
          color: rgba(255, 255, 255, 0.9);
          margin: 0;
        }
       
        .status-indicator {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-top: 8px;
        }
       
        .status-dot {
          height: 12px;
          width: 12px;
          border-radius: 50%;
          background: var(--yellow-400);
          box-shadow: 0 0 10px var(--yellow-400);
          animation: pulse 1.8s infinite;
        }
       
        .status-text {
          font-size: 14px;
          color: rgba(255, 255, 255, 0.9);
        }
       
        .close-button {
          height: 40px;
          width: 40px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.2);
          color: var(--white);
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s;
        }
       
        .close-button:hover {
          background: rgba(255, 255, 255, 0.35);
          transform: scale(1.1) rotate(180deg);
        }
       
        .messages-container {
          height: 440px;
          overflow-y: auto;
          padding: 24px;
          background: linear-gradient(to bottom, var(--gray-50), var(--white));
          display: flex;
          flex-direction: column;
          gap: 24px;
        }
       
        .message-wrapper {
          display: flex;
        }
       
        .message-wrapper-user {
          justify-content: flex-end;
        }
       
        .message-wrapper-bot {
          justify-content: flex-start;
        }
       
        .message-bubble {
          position: relative;
          max-width: 80%;
          border-radius: 24px;
          padding: 16px 20px;
          font-size: 16px;
          line-height: 1.7;
          box-shadow: var(--shadow-xl), var(--glow);
          transition: all 0.3s;
        }
       
        .message-bubble:hover {
          transform: translateY(-4px);
          box-shadow: var(--shadow-2xl), 0 0 20px rgba(14, 165, 233, 0.4);
        }
       
        .message-bubble-user {
          background: linear-gradient(100deg, var(--primary-teal), var(--primary-blue), var(--primary-indigo));
          color: var(--white);
          border-bottom-right-radius: 8px;
        }
       
        .message-bubble-bot {
          background: rgba(255, 255, 255, 0.98);
          color: var(--gray-800);
          border: 1px solid rgba(229, 231, 235, 0.8);
          border-bottom-left-radius: 8px;
        }
       
        .message-tail-bot {
          position: absolute;
          bottom: -12px;
          left: -12px;
          height: 24px;
          width: 24px;
          background: rgba(255, 255, 255, 0.98);
          border-bottom: 1px solid rgba(229, 231, 235, 0.8);
          border-left: 1px solid rgba(229, 231, 235, 0.8);
          border-bottom-left-radius: 50%;
          clip-path: polygon(0 0, 100% 0, 0 100%);
        }
       
        .message-tail-user {
          position: absolute;
          bottom: -12px;
          right: -12px;
          height: 24px;
          width: 24px;
          background: linear-gradient(100deg, var(--primary-teal), var(--primary-blue), var(--primary-indigo));
          border-bottom-right-radius: 50%;
          clip-path: polygon(0 0, 100% 0, 100% 100%);
        }
       
        .typing-indicator {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 16px;
          color: var(--gray-500);
          padding: 0 24px;
        }
       
        .typing-dots {
          display: flex;
          align-items: center;
          gap: 8px;
        }
       
        .typing-dot {
          height: 12px;
          width: 12px;
          border-radius: 50%;
          background: var(--primary-violet);
          box-shadow: 0 0 8px var(--primary-violet);
        }
       
        .input-area {
          border-top: 1px solid var(--gray-200);
          background: rgba(255, 255, 255, 0.98);
          padding: 16px 24px;
        }
       
        .input-container {
          display: flex;
          align-items: center;
          gap: 12px;
        }
       
        .message-input {
          flex: 1;
          border-radius: 20px;
          border: 1px solid var(--gray-300);
          background: var(--gray-50);
          padding: 16px 20px;
          font-size: 16px;
          outline: none;
          transition: all 0.3s;
        }
       
        .message-input:focus {
          border-color: var(--primary-violet);
          box-shadow: 0 0 0 4px rgba(139, 92, 246, 0.25);
        }
       
        .message-input::placeholder {
          color: var(--gray-500);
        }
       
        .message-input:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
       
        .send-button {
          border-radius: 20px;
          padding: 16px 28px;
          font-size: 16px;
          font-weight: 600;
          color: var(--white);
          border: none;
          cursor: pointer;
          box-shadow: var(--shadow-lg), var(--glow);
          transition: all 0.3s;
        }
       
        .send-button-enabled {
          background: linear-gradient(100deg, var(--primary-blue), var(--primary-indigo), var(--primary-violet));
        }
       
        .send-button-enabled:hover {
          background: linear-gradient(100deg, #0c8fd3, #5a5ae8, #7c3aed);
          box-shadow: var(--shadow-xl), 0 0 20px rgba(139, 92, 246, 0.5);
        }
       
        .send-button-disabled {
          background: var(--gray-300);
          cursor: not-allowed;
        }
       
        .button-content {
          display: flex;
          align-items: center;
          gap: 12px;
        }
       
        .spinner {
          height: 16px;
          width: 16px;
          border-radius: 50%;
          border: 4px solid var(--white);
          border-top-color: transparent;
          animation: spin 0.8s linear infinite;
        }
       
        .tip-text {
          font-size: 14px;
          color: var(--gray-500);
          text-align: center;
          margin-top: 16px;
        }
       
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
       
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
       
        @keyframes rotate-glow {
          0% { box-shadow: 0 0 12px rgba(14, 165, 233, 0.5); }
          50% { box-shadow: 0 0 24px rgba(16, 185, 129, 0.7); }
          100% { box-shadow: 0 0 12px rgba(14, 165, 233, 0.5); }
        }
       
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
       
        @keyframes avatar-glow {
          0%, 100% { box-shadow: 0 0 20px rgba(255, 255, 255, 0.4); }
          50% { box-shadow: 0 0 30px rgba(255, 255, 255, 0.6); }
        }
       
        .paragraph {
          margin-bottom: 10px;
        }
       
        .paragraph:last-child {
          margin-bottom: 0;
        }
      `}</style>
      {/* Floating Button */}
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.15, rotate: 10 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => setOpen(true)}
        className="floating-button"
        aria-label="Open chat with Griach Tahar"
      >
        <span style={{ fontSize: "28px", transition: "transform 0.4s" }}>🤖</span>
        <motion.div
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          className="floating-badge"
        >
          💬
        </motion.div>
      </motion.button>
      {/* Chat Panel */}
      <AnimatePresence>
        {open && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
              className="backdrop"
            />
            {/* Chat Container */}
            <motion.div
              initial={{ opacity: 0, y: 50, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 50, scale: 0.9 }}
              transition={{ type: "spring", damping: 20, stiffness: 250 }}
              className="chat-container"
            >
              {/* Header */}
              <div className="chat-header">
                <div className="header-content">
                  <div className="avatar">
                    🤖
                  </div>
                  <div className="header-text">
                    <h3 className="header-name">Griach Tahar</h3>
                    <p className="header-title">Full Stack Developer & IT Specialist</p>
                    <div className="status-indicator">
                      <div className="status-dot"></div>
                      <span className="status-text">Online • Ready to Code</span>
                    </div>
                  </div>
                  <button
                    onClick={() => setOpen(false)}
                    className="close-button"
                    aria-label="Close chat"
                  >
                    ✕
                  </button>
                </div>
              </div>
              {/* Messages Container */}
              <div className="messages-container" ref={messagesContainerRef}>
                {messages.map((m, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 15, scale: 0.9 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ delay: i * 0.1 }}
                    className={`message-wrapper ${m.role === "user" ? "message-wrapper-user" : "message-wrapper-bot"}`}
                  >
                    <div
                      className={`message-bubble ${m.role === "user" ? "message-bubble-user" : "message-bubble-bot"}`}
                    >
                      {m.text.split('\n').map((line, index) => (
                        <p key={index} className="paragraph">
                          {line}
                        </p>
                      ))}
                      {m.role === "bot" && (
                        <div className="message-tail-bot"></div>
                      )}
                      {m.role === "user" && (
                        <div className="message-tail-user"></div>
                      )}
                    </div>
                  </motion.div>
                ))}
                {loading && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="typing-indicator"
                  >
                    <div className="typing-dots">
                      <motion.div
                        animate={{ scale: [1, 1.4, 1] }}
                        transition={{ repeat: Infinity, duration: 0.7 }}
                        className="typing-dot"
                      />
                      <motion.div
                        animate={{ scale: [1, 1.4, 1] }}
                        transition={{ repeat: Infinity, duration: 0.7, delay: 0.25 }}
                        className="typing-dot"
                      />
                      <motion.div
                        animate={{ scale: [1, 1.4, 1] }}
                        transition={{ repeat: Infinity, duration: 0.7, delay: 0.5 }}
                        className="typing-dot"
                      />
                    </div>
                    <span>Griach Tahar is typing...</span>
                  </motion.div>
                )}
              </div>
              {/* Input Area */}
              <div className="input-area">
                <div className="input-container">
                  <input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                    placeholder="Ask about websites, development, or ideas..."
                    className="message-input"
                    disabled={loading}
                  />
                  <motion.button
                    whileTap={{ scale: 0.9 }}
                    whileHover={{ scale: 1.1 }}
                    onClick={sendMessage}
                    disabled={loading || !input.trim()}
                    className={`send-button ${loading || !input.trim() ? 'send-button-disabled' : 'send-button-enabled'}`}
                  >
                    <span className="button-content">
                      {loading ? (
                        <>
                          <div className="spinner"></div>
                          Sending
                        </>
                      ) : (
                        'Send'
                      )}
                    </span>
                  </motion.button>
                </div>
                <p className="tip-text">
                  💡 Tip: Inquire about tech stacks, designs, or projects
                </p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}